import {isArray, isDefined} from "./is-type";

export const toArray = <InputArrayContents = unknown>(inputValue?: InputArrayContents | InputArrayContents[]): InputArrayContents[] => {
  if (isArray<InputArrayContents>(inputValue)){
    // already an array, nothing to do
    return inputValue;
  }
  if (isDefined<InputArrayContents>(inputValue)) {
    // have value, return it in array
    return [inputValue];
  }
  // no input value, return empty array
  return [];
}

export const isArrayEmpty = <InputArray extends ArrayLike<any> = unknown[]>(inputValue?: InputArray): boolean =>
  toArray(inputValue).length === 0;

export const deepCopyArray = <Type>(inputValue: Type): Type => JSON.parse(JSON.stringify(inputValue)) as Type;

const defaultArrayElementCompareFn = <T>(a: T, b: T) => { return a === b; };

/**
 * Updates or inserts an item into an array based on a comparison function.
 *
 * @template T - The type of elements in the array.
 * @param {object} options - The options for the upsert operation.
 * @param {T[]} options.array - The array to perform the upsert operation on.
 * @param {T} options.item - The item to insert or update in the array.
 * @param {(a: T, b: T) => boolean} options.compareFn - A function to compare two items in the array.
 *        Defaults to strict equality comparison.
 * @returns {T[]} A new array with the item inserted or updated.
 *
 * @example
 * const array = [{ id: 1 }, { id: 2 }];
 * const newItem = { id: 2, value: 'updated' };
 * const result = arrayUpsert({
 *   array,
 *   item: newItem,
 *   compareFn: (a, b) => a.id === b.id
 * });
 * // result: [{ id: 1 }, { id: 2, value: 'updated' }]
 */
export const arrayUpsert = <T>(options: {array: T[], item: T, compareFn: (a: T, b: T) => boolean }): T[] => {
  const {array, item, compareFn = defaultArrayElementCompareFn} = options;
  const index = array.findIndex((i) => compareFn(i, item));
  if (index === -1) {
    return [...array, item];
  }
  return array.map((i, idx) => (idx === index ? item : i));
};


/**
 * Removes an item from an array based on a comparison function.
 *
 * @template T - The type of elements in the array.
 * @param {object} options - The options for the remove operation.
 * @param {T[]} options.array - The array to remove the item from.
 * @param {T} options.item - The item to remove from the array.
 * @param {(a: T, b: T) => boolean} options.compareFn - A function to compare two items in the array.
 *        Defaults to strict equality comparison.
 * @returns {T[]} A copy of the array with the item removed.
 *
 * @example
 * const array = [{ id: 1 }, { id: 2 }];
 * const itemToRemove = { id: 2 };
 * const result = arrayRemove({
 *   array,
 *   item: itemToRemove,
 *   compareFn: (a, b) => a.id === b.id
 * });
 * // result: [{ id: 1 }]
 */
export const arrayRemove = <T>(options: {array: T[], item: T, compareFn: (a: T, b: T) => boolean }): T[] => {
  const {array, item, compareFn = defaultArrayElementCompareFn} = options;
  const index = array.findIndex((i) => compareFn(i, item));
  if (index === -1) {
    return array;
  }
  return [...array.slice(0, index), ...array.slice(index + 1)];
};